import React, { memo, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, IconButton, Chip, Tooltip, Checkbox, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import ListThumbnail from './ListThumbnail';
import { formatDisplayValue } from './DataListItems';

// =====================================================================
// 1. COMPONENT ITEM SIÊU GỌN (COMPACT LIST ITEM)
// =====================================================================
const CompactListItem = memo(({ item, index, fields, context, onEdit, onCopy, onDelete, onView, hasImageField, renderItem, isSelectionMode, isItemSelected, onSelectionChange, history, renderRowActions }) => {
  const excludedFields = ['ten_trang_thai', 'color', 'picture', 'hinh_anh'];
  
  let titleField = fields.find(f => f.isTitle === true) || fields.find(f => f.type === 'String' && !excludedFields.includes(f.field)) || fields[0];
  const subTitleFields = fields.filter(f => f.isSubTitle === true);
  const actionField = fields.find(f => f.type === 'Action');

  const statusColor = item.color || null;
  const statusLabel = item.ten_trang_thai || null;

  const handleCheckbox = (e) => {
    if (onSelectionChange) onSelectionChange(e, item._id);
  };

  const renderValue = (f, data) => {
    const rawValue = formatDisplayValue(data[f.field], f, data, context);
    if (f.type === 'RichText') {
      return <span dangerouslySetInnerHTML={{ __html: rawValue }} />;
    }
    return rawValue;
  };

  if (typeof renderItem === 'function') {
    return (
      <Box sx={{ position: 'relative', borderBottom: '1px solid #e0e0e0', p: 1 }}>
         {renderItem(item, index, () => onEdit && onEdit(item), () => onDelete && onDelete(item._id), () => onView && onView(item), () => onCopy && onCopy(item))}
      </Box>
    );
  }

  // 👇 BIẾN KIỂM TRA XEM DÒNG CÓ THỂ CLICK ĐƯỢC KHÔNG
  const isClickable = !!onView || !!onEdit;

  return (
    <Box 
      // 👇 THAY ĐỔI: Ưu tiên gọi onView, nếu không có thì gọi onEdit
      onClick={() => { 
        if (onView) {
          onView(item); 
        } else if (onEdit) {
          onEdit(item);
        }
      }}
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative', 
        bgcolor: '#ffffff', 
        borderBottom: '1px solid #e5e5ea',
        py: 1, 
        px: 1.5,
        // 👇 THAY ĐỔI: Hiện con trỏ tay nếu có 1 trong 2 quyền Xem hoặc Sửa
        cursor: isClickable ? 'pointer' : 'default', 
        transition: 'background-color 0.2s',
        // Chỉ hiện màu xám khi di chuột vào nếu dòng đó có thể click
        '&:hover': { bgcolor: isClickable ? '#f8f9fa' : '#ffffff' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1 }}>
        
        {isSelectionMode && (
           <Box 
             sx={{ flexShrink: 0, mt: 0.5 }}
             onClick={(e) => e.stopPropagation()} 
           >
             <Checkbox checked={isItemSelected} onChange={handleCheckbox} size="small" sx={{ p: 0 }} />
           </Box>
        )}

        {statusColor && <Box sx={{ width: 4, height: 32, borderRadius: 2, bgcolor: statusColor, flexShrink: 0, mt: 0.5 }} />}
        
        {hasImageField && (
          <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 1.5, overflow: 'hidden', bgcolor: '#f2f2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', '& img': { width: '100%', height: '100%', objectFit: 'cover' } }}>
            <ListThumbnail item={item} fields={fields} context={context} altText={titleField ? String(item[titleField.field]) : "Image"} />
          </Box>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography 
            variant="subtitle2" 
            fontWeight="bold" 
            color="primary.main" 
            sx={{ lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {titleField ? renderValue(titleField, item) : `Bản ghi #${index + 1}`}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'nowrap', overflow: 'hidden' }}>
            {subTitleFields.length > 0 && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                noWrap 
                sx={{ flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {subTitleFields.map((f, idx) => (
                  <React.Fragment key={f.field}>
                    {renderValue(f, item)}
                    {idx < subTitleFields.length - 1 && (
                      <span style={{ margin: '0 6px', opacity: 0.4, fontSize: '0.9em' }}>•</span>
                    )}
                  </React.Fragment>
                ))}
              </Typography>
            )}
            {statusLabel && (
              <Chip label={statusLabel} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 'bold', bgcolor: statusColor || '#8e8e93', color: 'white', flexShrink: 0 }} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignSelf: 'flex-start' }}>
           {renderRowActions ? renderRowActions(item, 'list') : null}
           {onCopy && (
             <IconButton 
               size="small" 
               onClick={(e) => { e.stopPropagation(); onCopy(item); }} 
               sx={{ color: '#34c759', p: 0.5 }}
             >
               <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
             </IconButton>
           )}

           {/* Vẫn giữ nút Edit ở ngoài để người dùng biết có thể sửa nhanh */}
           {onEdit && (
             <IconButton 
               size="small" 
               onClick={(e) => { e.stopPropagation(); onEdit(item); }} 
               sx={{ color: '#ff9500', p: 0.5 }}
             >
               <EditIcon sx={{ fontSize: 16 }} />
             </IconButton>
           )}

           {onDelete && (
             <IconButton 
               size="small" 
               onClick={(e) => { e.stopPropagation(); onDelete(item._id); }} 
               sx={{ color: '#ff3b30', p: 0.5 }}
             >
               <DeleteOutlineIcon sx={{ fontSize: 16 }} />
             </IconButton>
           )}
        </Box>
      </Box>

      {actionField && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', pl: statusColor ? 1 : 0 }}>
           <Button 
            variant="contained" 
            size="small" 
            color="primary"
            sx={{ textTransform: 'none', py: 0, px: 1.5, fontSize: '0.75rem', height: 26, borderRadius: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              const actionFn = actionField.actionScript;
              if (actionFn && typeof(actionFn) === "function") {
                actionFn(context, item, history);
              }
            }}
          >
            {actionField.label || 'Thực thi'}
          </Button>
        </Box>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.isItemSelected === nextProps.isItemSelected &&
    prevProps.isSelectionMode === nextProps.isSelectionMode &&
    prevProps.renderRowActions === nextProps.renderRowActions
  );
});

// =====================================================================
// 2. COMPONENT LIST BAO BỌC BÊN NGOÀI
// =====================================================================
export default function CompactListView({
  data, fields, 
  isSelectionMode, selectedIds, onSelectionChange,
  onView, onEdit, onCopy, onDelete, canEdit, canCopy, canDelete, context, renderItem,
  renderRowActions,   // nút tuỳ chỉnh của dòng (rowActions)
}) {
  const history = useHistory();
  const hasImageField = fields.some(f => f.field === 'picture' || f.field === 'hinh_anh' || f.type === 'File' || f.type === 'FileArray');

  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const handleToggleSingleCard = useCallback((event, id) => {
    const currentSelected = selectedIdsRef.current;
    const selectedIndex = currentSelected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) newSelected = newSelected.concat(currentSelected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(currentSelected.slice(1));
    else if (selectedIndex === currentSelected.length - 1) newSelected = newSelected.concat(currentSelected.slice(0, -1));
    else if (selectedIndex > 0) newSelected = newSelected.concat(currentSelected.slice(0, selectedIndex), currentSelected.slice(selectedIndex + 1));

    if (onSelectionChange) onSelectionChange(newSelected);
  }, [onSelectionChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', bgcolor: '#ffffff', borderRadius: 2, overflow: 'hidden' }}>
      {data.map((item, index) => {
        const isItemSelected = selectedIds?.indexOf(item._id) !== -1;
        return (
          <CompactListItem
            key={item._id || index}
            renderItem={renderItem}
            isItemSelected={isItemSelected}
            onSelectionChange={onSelectionChange ? handleToggleSingleCard : null}
            isSelectionMode={isSelectionMode}
            item={item}
            index={index}
            fields={fields}
            context={context}
            history={history} 
            onEdit={canEdit ? onEdit : null}
            onDelete={canDelete ? onDelete : null}
            onCopy={canCopy ? onCopy : null}
            onView={onView}
            hasImageField={hasImageField}
            renderRowActions={renderRowActions}
          />
        );
      })}
    </Box>
  );
}